import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChartTypeKeyboard } from "./useChartTypeKeyboard";
import { ReactComponent as Info } from "../../../assets/svg/info.svg";
import {
  GetOrders200Response,
  InstrumentTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { LAYER_COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { useOrder } from "../../../hooks/api/order/useOrder";
import usePositionsWSS from "../../../hooks/wss/usePositionsWSS";
import { getContractPriceStep } from "../../../utils/instruments";
import usePersistentState from "../../../hooks/usePersistentState";
import { ButtonThemeEnum } from "../../Buttons/styles";
import RemoveOrderModal from "../../ConfirmationModal/RemoveOrderModal";
import FundingChart from "../../DepthFundingChart/FundingChart";
import TradingViewChart from "../../TradingViewChart";
import SegmentedControl from "../../shared/SegmentedControl";
import DepthSection from "../DepthSection";
import TradeOrderbookSection from "../TradeOrderbookSection";
import { OrderbookLayoutEnum } from "../TradeOrderbookSection/shared";
import {
  AdaptiveWrapper,
  ChartArea,
  ChartContainer,
  ChartTab,
  ChartTabWrapper,
  ChartsContainer,
  Header,
  MarketDetailsButton,
  OrderbookContainer,
} from "./style";
import MarketDetailsModal, {
  useResolvedInstrumentType,
} from "./MarketDetailsModal";

interface IPerpsOrderbookAndChartProps {
  onOrderbookRowClick: (
    price: string,
    size: string,
    side: SideResponse
  ) => void;
  perpInstrument?: IPerpsMarket;
  height?: string;
  instrumentType?: InstrumentTypeResponse;
}

export enum ChartTypeEnum {
  PRICE = "price_chart",
  DEPTH = "depth_chart",
  FUNDING = "funding_chart",
}

function PerpsOrderbookAndChart({
  onOrderbookRowClick,
  height,
  perpInstrument,
  instrumentType,
}: IPerpsOrderbookAndChartProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "Perps.PerpsOrderbookAndChart",
  });
  const [currentChartType, setCurrentChartType] = useState<ChartTypeEnum>(
    ChartTypeEnum.PRICE
  );

  const prelaunch = perpInstrument?.pre_launch;
  const { orderbookLayout, setOrderbookLayout } = usePersistentState();
  const layout = (orderbookLayout as OrderbookLayoutEnum) || OrderbookLayoutEnum.TAB;
  const isLargeLayout = layout === OrderbookLayoutEnum.LARGE;

  const { positionsMap } = usePositionsWSS();
  const { data: orderData } = useOrder();
  // The market's own type, never a Perpetual fallback (R-C); the Market
  // Details modal reads /statistics with it.
  const resolvedType = useResolvedInstrumentType(instrumentType, perpInstrument);

  const [removingOrder, setRemovingOrder] = useState<GetOrders200Response>();
  const [showMarketDetails, setShowMarketDetails] = useState(false);

  const onCancelOrder = useCallback((order: GetOrders200Response) => {
    setRemovingOrder(order);
  }, []);

  // Stable across renders: the chart redraws every order line when this changes.
  const instrumentOrders = useMemo(
    () =>
      orderData?.filter(
        (o) => o.instrument_id === perpInstrument?.instrument_id
      ),
    [orderData, perpInstrument?.instrument_id]
  );

  const onClickMarketDetails = useCallback(() => {
    setShowMarketDetails(true);
  }, []);

  const controls = useMemo(() => {
    const HIGH_LOAD_MODE = process.env.REACT_APP_HIGH_LOAD_MODE === "true";

    let keys = Object.keys(ChartTypeEnum);
    if (HIGH_LOAD_MODE || resolvedType === InstrumentTypeResponse.Spot) {
      keys = keys.filter((k) => k !== "FUNDING");
    }

    const values = keys.map((k) => {
      const key = k as keyof typeof ChartTypeEnum;

      return {
        value: ChartTypeEnum[key],
        display: <ChartTab>{t(ChartTypeEnum[key])}</ChartTab>,
        textColor:
          currentChartType === ChartTypeEnum[key]
            ? TEXT_COLORS.one
            : TEXT_COLORS.three,
      };
    });
    if (prelaunch) {
      return values.slice(0, values.length - 1);
    }
    return values;
  }, [prelaunch, t, currentChartType, resolvedType]);

  // [ / ] switch the chart-type strip while hovering the chart area.
  const chartGateRef = useChartTypeKeyboard(
    controls,
    currentChartType,
    setCurrentChartType,
  );

  return (
    <>
      <RemoveOrderModal
        order={removingOrder}
        onHide={() => setRemovingOrder(undefined)}
        show={!!removingOrder}
      />
      <MarketDetailsModal
        perpInstrument={perpInstrument}
        instrumentType={resolvedType}
        show={showMarketDetails}
        onHide={() => setShowMarketDetails(false)}
      />
      {/* Single render path — AdaptiveWrapper uses display:contents in
          non-Large mode so ChartArea/OrderbookContainer remain direct grid
          items. In Large mode it becomes a flex row spanning both columns. */}
      <AdaptiveWrapper
        isLarge={isLargeLayout}
        style={isLargeLayout ? { height } : undefined}
      >
        <ChartArea
          ref={chartGateRef}
          style={isLargeLayout ? { flex: 1, height: "100%" } : { height }}
        >
          <Header>
            <ChartTabWrapper>
              <SegmentedControl
                segments={controls}
                value={currentChartType}
                onSelect={(e) => setCurrentChartType(e as ChartTypeEnum)}
                config={{
                  widthType: "fullWidth",
                  backgroundColor: LAYER_COLORS.two,
                  activeBackgroundColor: LAYER_COLORS.three,
                  borderRadius: "10px",
                  button: {
                    fontSize: FONT_SIZE.one,
                  },
                }}
              />
              <MarketDetailsButton
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                onClick={onClickMarketDetails}
              >
                {t("market_details")}
                <Info />
              </MarketDetailsButton>
            </ChartTabWrapper>
          </Header>
          {perpInstrument && (
            <ChartsContainer>
              <TradingViewChart
                key={perpInstrument?.instrument_id}
                hidden={currentChartType !== ChartTypeEnum.PRICE}
                amountDecimals={
                  getContractPriceStep(perpInstrument).amount_precision
                }
                priceDecimals={
                  getContractPriceStep(perpInstrument).price_precision
                }
                symbol={perpInstrument.instrument_name}
                position={positionsMap[perpInstrument.instrument_name]}
                orders={instrumentOrders}
                onCancelOrder={onCancelOrder}
              />
              {currentChartType === ChartTypeEnum.DEPTH && (
                <ChartContainer $hidden={false}>
                  <DepthSection perpInstrument={perpInstrument} />
                </ChartContainer>
              )}
              {currentChartType === ChartTypeEnum.FUNDING && (
                <ChartContainer
                  $hidden={currentChartType !== ChartTypeEnum.FUNDING}
                >
                  <FundingChart instrument={perpInstrument.instrument_name} />
                </ChartContainer>
              )}
            </ChartsContainer>
          )}
        </ChartArea>
        <OrderbookContainer
          isLarge={isLargeLayout}
          style={{ height: isLargeLayout ? "100%" : height }}
        >
          <TradeOrderbookSection
            perpInstrument={perpInstrument}
            onOrderbookRowClick={onOrderbookRowClick}
            layout={layout}
            onLayoutChange={(l) => setOrderbookLayout(l)}
          />
        </OrderbookContainer>
      </AdaptiveWrapper>
    </>
  );
}

export default PerpsOrderbookAndChart;
